import { useMemo } from 'react'
import { Box, Button, Flex, Label, Loading, Select, Text, Tooltip } from 'ui'
import { toTitleCase } from 'lib/string'
import { CompaniesTable } from './CompaniesTable'
import { useEditMemberContext } from '../context/EditMemberProvider'
import { useAuthContext } from 'components/auth'
import { getAddRemoveDisabledTooltip } from '../addRemoveDisabled'

const AddCompanyAccessRow = ({ isDisabled }: { isDisabled: boolean }) => {
  const { company } = useAuthContext()
  const {
    state: { companyOptions, isPending, selectedCompanyId },
    actions: { addCompany, setSelectedCompanyId },
  } = useEditMemberContext()

  const handleAddCompanyAccess = () => {
    if (!selectedCompanyId) {
      return
    }
    addCompany(selectedCompanyId)
  }

  const orderedCompanyOptions = useMemo(() => {
    // Current company should be first
    return [...companyOptions].sort((a, b) => {
      if (a.value === Number(company?.id)) {
        return -1
      }
      return a.label.localeCompare(b.label)
    })
  }, [companyOptions, company?.id])

  return (
    <Flex flexDirection="row" alignItems="center" gap={2}>
      <Box flex={1}>
        <Select
          options={orderedCompanyOptions}
          value={companyOptions.find((c) => c.value === selectedCompanyId)}
          onChange={(c) => {
            if (c?.value) {
              setSelectedCompanyId(c.value)
            }
          }}
          isDisabled={isDisabled}
          formatOptionLabel={(option) => {
            const isCurrentCompany = option.value === Number(company?.id)
            return (
              <Flex
                flexDirection="row"
                alignItems="center"
                gap={2}
                data-value={option.value}
              >
                <Text>{option.label}</Text>
                {!!isCurrentCompany && (
                  <Label variant="success" size="small">
                    Current
                  </Label>
                )}
              </Flex>
            )
          }}
        />
      </Box>
      <Button
        onClick={handleAddCompanyAccess}
        disabled={isDisabled || !selectedCompanyId || isPending}
        loading={isPending}
      >
        Add
      </Button>
    </Flex>
  )
}

export const EditCompanyGroupMemberForm = () => {
  const {
    state: { member, addRemoveDisabledReason },
  } = useEditMemberContext()

  const isAddRemoveDisabled = addRemoveDisabledReason !== null
  const addRemoveTooltip = addRemoveDisabledReason
    ? getAddRemoveDisabledTooltip(addRemoveDisabledReason)
    : ''

  if (!member) {
    return <Loading />
  }

  return (
    <Flex flexDirection="column" gap={3}>
      <Flex flexDirection="column" gap={1}>
        <Text>{toTitleCase(member.name)}</Text>
        <Text color="lightText">{member.email}</Text>
        <Text color="lightText">
          Role: {member.companyRolePermission?.description}
        </Text>
      </Flex>
      <Flex flexDirection="column" gap={1}>
        <Text>Add company access</Text>
        {isAddRemoveDisabled ? (
          <Tooltip content={addRemoveTooltip}>
            <Box width="100%">
              <AddCompanyAccessRow isDisabled />
            </Box>
          </Tooltip>
        ) : (
          <AddCompanyAccessRow isDisabled={false} />
        )}
      </Flex>
      <CompaniesTable />
    </Flex>
  )
}
